// frontend/src/pages/Register.jsx
// Self-registration page — 14-day trial.
// Module 8 patch: inline password mismatch validation, show/hide toggle,
//                 ABN checksum validation (Australian standard).
// IP-009 patch: unticked-by-default Master Subscription Agreement
//               checkbox, required before submission is enabled.
// commit: feat(ip009): add Master Subscription Agreement checkbox to registration form
import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../api';

const APRA_ENTITY_TYPES = [
  { value: 'adi',     label: 'Authorised Deposit-taking Institution (ADI)' },
  { value: 'insurer', label: 'General or Life Insurer' },
  { value: 'rse',     label: 'RSE Licensee (Superannuation)' },
  { value: 'fintech', label: 'Fintech / Payment Provider' },
  { value: 'other',   label: 'Other financial services entity' },
];

// Australian ABN checksum validation
function validateABN(raw) {
  const digits = raw.replace(/[\s\-]/g, '');
  if (digits.length !== 11 || !/^\d+$/.test(digits)) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const d = digits.split('').map(Number);
  d[0] -= 1;
  const total = weights.reduce((sum, w, i) => sum + w * d[i], 0);
  return total % 89 === 0;
}

export default function Register({ onLogin }) {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    orgName:         '',
    abn:             '',
    apraEntityType:  'adi',
    adminName:       '',
    adminEmail:      '',
    adminPassword:   '',
    confirmPassword: '',
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [showPwd,    setShowPwd]    = useState(false);
  const [showConf,   setShowConf]   = useState(false);
  const [pwdError,   setPwdError]   = useState('');   // inline password field error
  const [abnError,   setAbnError]   = useState('');   // inline ABN error
  const [submitErr,  setSubmitErr]  = useState('');   // top-level submit error
  const [loading,    setLoading]    = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setSubmitErr('');

    // Inline ABN validation on change
    if (name === 'abn') {
      const stripped = value.replace(/[\s\-]/g, '');
      if (stripped.length > 0 && stripped.length < 11) {
        setAbnError('ABN must be 11 digits.');
      } else if (stripped.length === 11 && !validateABN(value)) {
        setAbnError('ABN checksum is invalid. Please check the number.');
      } else {
        setAbnError('');
      }
    }

    // Inline confirm password check
    if (name === 'confirmPassword') {
      setPwdError(value && value !== form.adminPassword ? 'Passwords do not match.' : '');
    }
    if (name === 'adminPassword') {
      setPwdError(form.confirmPassword && value !== form.confirmPassword ? 'Passwords do not match.' : '');
    }
  }, [form.adminPassword, form.confirmPassword]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitErr('');

    // Final validation before submit
    if (form.abn && !validateABN(form.abn)) {
      setAbnError('ABN checksum is invalid. Please check the number.');
      return;
    }
    if (form.adminPassword !== form.confirmPassword) {
      setPwdError('Passwords do not match.');
      return;
    }
    if (form.adminPassword.length < 12) {
      setPwdError('Password must be at least 12 characters.');
      return;
    }
    if (!agreedToTerms) {
      setSubmitErr('You must accept the Master Subscription Agreement to register.');
      return;
    }

    setLoading(true);
    try {
      const { token, user, org } = await register({
        orgName:        form.orgName.trim(),
        abn:            form.abn.trim() || undefined,
        apraEntityType: form.apraEntityType,
        adminName:      form.adminName.trim(),
        adminEmail:     form.adminEmail.trim(),
        adminPassword:  form.adminPassword,
        agreedToTerms,
      });
      localStorage.setItem('token', token);
      onLogin({ ...user, org_id: org.id, org_name: org.name, trial_ends_at: org.trial_ends_at });
      navigate('/dashboard');
    } catch (err) {
      setSubmitErr(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const hasErrors = Boolean(pwdError || abnError);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f2ed', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>
            Flow Advisory Group
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#132e50', marginBottom: 8 }}>
            AI Governance Platform
          </div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            Start your 14-day free trial — no payment required
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: '32px 36px', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>

          {submitErr && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 20 }}>
              {submitErr}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>

            <SectionLabel>Organisation</SectionLabel>

            <Field label="Organisation name *" name="orgName" value={form.orgName} onChange={handleChange} placeholder="e.g. Acme Bank Ltd" required />

            {/* ABN with inline error */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>ABN <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input
                name="abn"
                value={form.abn}
                onChange={handleChange}
                placeholder="e.g. 51 824 753 556"
                style={{ ...inputStyle, borderColor: abnError ? '#fca5a5' : '#d1d5db' }}
              />
              {abnError && <div style={inlineErrStyle}>{abnError}</div>}
              {!abnError && form.abn && validateABN(form.abn) && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ Valid ABN</div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>APRA entity type *</label>
              <select name="apraEntityType" value={form.apraEntityType} onChange={handleChange} style={inputStyle} required>
                {APRA_ENTITY_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <SectionLabel style={{ marginTop: 24 }}>Your account</SectionLabel>

            <Field label="Full name *" name="adminName" value={form.adminName} onChange={handleChange} placeholder="e.g. Jane Smith" required />
            <Field label="Work email *" name="adminEmail" type="email" value={form.adminEmail} onChange={handleChange} placeholder="you@yourorg.com.au" required />

            {/* Password with show/hide */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Password * <span style={{ color: '#9ca3af', fontWeight: 400 }}>(min 12 characters)</span></label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  placeholder="••••••••••••"
                  style={{ ...inputStyle, paddingRight: 40, borderColor: pwdError ? '#fca5a5' : '#d1d5db' }}
                  required
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPwd(v => !v)} style={eyeBtn} title={showPwd ? 'Hide password' : 'Show password'}>
                  {showPwd ? '🙈' : '👁️'}
                </button>
              </div>
              {form.adminPassword && form.adminPassword.length < 12 && (
                <div style={inlineErrStyle}>Password must be at least 12 characters.</div>
              )}
            </div>

            {/* Confirm password with show/hide */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Confirm password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConf ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••••••"
                  style={{ ...inputStyle, paddingRight: 40, borderColor: pwdError ? '#fca5a5' : '#d1d5db' }}
                  required
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowConf(v => !v)} style={eyeBtn} title={showConf ? 'Hide password' : 'Show password'}>
                  {showConf ? '🙈' : '👁️'}
                </button>
              </div>
              {pwdError && <div style={inlineErrStyle}>{pwdError}</div>}
              {!pwdError && form.confirmPassword && form.confirmPassword === form.adminPassword && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✓ Passwords match</div>
              )}
            </div>

            {/* Master Subscription Agreement acceptance, unticked by default (IP-009) */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <input
                type="checkbox"
                id="agreedToTerms"
                checked={agreedToTerms}
                onChange={e => { setAgreedToTerms(e.target.checked); setSubmitErr(''); }}
                style={{ marginTop: 3 }}
                required
              />
              <label htmlFor="agreedToTerms" style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                I have read and agree to the{' '}
                <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: '#132e50', fontWeight: 600 }}>
                  Master Subscription Agreement
                </a>.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || hasErrors || !agreedToTerms}
              style={{
                width: '100%', padding: '12px',
                background: (loading || hasErrors || !agreedToTerms) ? '#9ca3af' : '#132e50',
                color: '#fff', border: 'none', borderRadius: 6,
                fontSize: 14, fontWeight: 700,
                cursor: (loading || hasErrors || !agreedToTerms) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating your account…' : 'Start free trial'}
            </button>
          </form>

          <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#132e50', fontWeight: 600 }}>Sign in</Link>
          </div>
        </div>

        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
          14-day trial. No credit card required. Your data is retained after the trial ends.
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ marginBottom: 8, fontSize: 11, fontWeight: 700, color: '#4a6280', letterSpacing: 0.6, textTransform: 'uppercase' }}>
      {children}
    </div>
  );
}

function Field({ label, name, value, onChange, type = 'text', placeholder, required }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} style={inputStyle} required={required} />
    </div>
  );
}

const labelStyle   = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 };
const inputStyle   = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#132e50', background: '#fff', outline: 'none' };
const inlineErrStyle = { fontSize: 11, color: '#dc2626', marginTop: 4 };
const eyeBtn       = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, color: '#6b7280' };
